// React
import React, { useEffect, useState, useRef, useMemo } from 'react';

// Material UI
import {
    Box,

} from '@mui/material';


// Componentes locales
import { type CommentInputRef } from './commentInput';
import CommentItem from './commentItem';
import { type FeedbackInputRef } from './feedbackInput';
import TicketInfoPanel from './comments/ticketInfoPanel';
import TicketDocuments from './comments/ticketDocuments';
import TicketResolutionPanel from './comments/ticketResolutionPanel';
import CommentListSection from './comments/commentListSection';
import AttachedFilesPreview from './comments/attachedFilesPreview';
import PreviewModal from './comments/previewModal';
import CommentInputFooter from './comments/commentInputFooter';
import AttachedFileList from './comments/attachedFileList';

// Servicios y utilidades

import NOTIFICATION_SYSTEM from '../../../../main_components/alerts/notification_sistem';

import { useTicketStatus } from './hooks/useTicketStatus';
import { useAudioRecorder } from './hooks/useAudioRecorder';
import { useResolution } from './hooks/useResolution';
import { useAttachments } from './hooks/useAttachments';
import { useTicketDocuments } from './hooks/useTicketDocuments';
import { useComments } from './hooks/useComments';

// Tipos
interface TicketListProps {
    tickets: any;
    onTicketUpdate?: (updatedTicket: any) => void;
}


const COMMENTS_SUPPORT: React.FC<TicketListProps> = ({ tickets, onTicketUpdate }) => {
    // Refs
    const commentInputRef = useRef<CommentInputRef>(null);
    const feedbackInputRef = useRef<FeedbackInputRef>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const notificationSystemRef = useRef<any>(null);

    // useState
    const [docsData, setdocsData] = useState<any>(null);
    const [previewFile, setPreviewFile] = useState<{ url: string; type: string } | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [currentStatus, setCurrentStatus] = useState(tickets?.status);

    // Popover control
    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const open = Boolean(anchorEl);
    const id = open ? 'description-popover' : '';

    // Funciones auxiliares

    const getCommentDepth = (id: number | null, all: any[], currentDepth = 0): number => {
        if (id === null) return currentDepth;
        const parent = all.find(c => c.id_comment === id);
        if (!parent) return currentDepth;
        return getCommentDepth(parent.reply, all, currentDepth + 1);
    };

    const getCurrentTimestamp = (): string => {
        const now = new Date();
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    };




    // Hooks

    const {
        attachedFiles,
        setAttachedFiles,
        handleFileChange,
        removeFile,
        formatFileNameByType,
    } = useAttachments({ notificationSystemRef });

    const {
        comments,
        loadingComments,
        error,
        replyTo,
        isEditing,
        editingComment,
        isSubmitting,
        get_comments,
        handleSubmitComment,
        handleReply,
        handleEdit,
        setReplyTo,
        setIsEditing,
        setEditingComment,
        setLoadingComments,
        setIsSubmitting,
        setComments
    } = useComments({
        ticketId: tickets.ticket,
        ticketData: tickets,
        onTicketUpdate,
        commentInputRef,
        notificationSystemRef,
        attachedFiles,
        setAttachedFiles,
        setDocsData: setdocsData,
        getCurrentTimestamp,
    });

    const rootComments = useMemo(() => {
        return comments.filter(c => c.reply === null);
    }, [comments]);


    const {
        ticketDocuments,
        loadingTicketDocuments,
        pendingTicketFiles,
        uploadingFiles,
        setPendingTicketFiles,
        loadTicketDocuments,
        handleTicketFileUpload,
        handleUploadPendingTicketFiles,
        removePendingFile,
        setLoadingTicketDocuments
    } = useTicketDocuments({
        ticketData: tickets,
        notificationSystemRef,
        onTicketUpdate,
        getCurrentTimestamp,
        formatFileNameByType,
    });


    const {
        isRecording,
        recordingTime,
        toggleRecording: handleAudioRecording,
    } = useAudioRecorder({
        onAudioReady: (audioFile) => setAttachedFiles((prev) => [...prev, audioFile]),
        formatFileNameByType,
        notificationSystemRef,
    });


    const {
        resolving,
        setResolving,
        resolvingLoading,
        rating,
        setRating,
        feedback,
        setFeedback,
        handleConfirmResolution
    } = useResolution({
        ticketData: tickets,
        onTicketUpdate,
        getCurrentTimestamp,
        setLoadingComments,
        setLoadingTicketDocuments,
        setIsSubmitting,
        notificationSystemRef,
        setCurrentStatus
    });

    const {
        editingStatus,
        setEditingStatus,
        statusUpdating,
        setStatusUpdating,
        showReopenConfirm,
        setShowReopenConfirm,
        confirmReopenTicket,
    } = useTicketStatus({
        ticketData: tickets,
        onTicketUpdate,
        getCurrentTimestamp,
        notificationSystemRef,
        setCurrentStatus,
    });

    const renderComment = (cmt: any, depth = 0) => (
        <CommentItem
            key={cmt.id_comment}
            comment={cmt}
            depth={depth}
            onReply={handleReply}
            onEdit={handleEdit}
            renderAttachedFiles={(fileNames) => (
                <AttachedFileList
                    fileNames={fileNames}
                    docsData={docsData}
                    ticketId={tickets.ticket}
                    setDocsData={setdocsData}
                    setPreviewFile={setPreviewFile}
                    notificationSystemRef={notificationSystemRef}
                />
            )}
            renderComment={renderComment}
        />
    );



    useEffect(() => {
        get_comments();
        loadTicketDocuments();
    }, [tickets?.ticket]);

    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [comments]);




    return (
        <>
            <NOTIFICATION_SYSTEM notificationSystemRef={notificationSystemRef}></NOTIFICATION_SYSTEM>
            <Box display="flex" height="100%" p={2} bgcolor="#F9FAFB">

                <Box flex={3} pr={3} display="flex" flexDirection="column" height="645px" overflow="hidden">

                    {/* Comentarios y estados de respuesta/edición */}
                    <CommentListSection
                        key={rootComments?.length+"comments"+comments?.length}
                        loadingComments={loadingComments}
                        error={error}
                        comments={comments}
                        rootComments={rootComments}
                        renderComment={renderComment}
                        tickets={tickets}
                        scrollRef={scrollRef}
                        replyTo={replyTo}
                        isEditing={isEditing}
                        editingComment={editingComment}
                        onCancelReply={() => setReplyTo(null)}
                        onCancelEdit={() => {
                            setIsEditing(false);
                            setEditingComment(null);
                        }}
                        attachedFilesCount={attachedFiles.length}
                    />

                    {/* Archivos adjuntos previos al envío */}
                    <AttachedFilesPreview
                        attachedFiles={attachedFiles}
                        isSubmitting={isSubmitting}
                        removeFile={removeFile}
                        setPreviewFile={setPreviewFile}
                    />

                    {/* Modal de vista previa */}
                    <PreviewModal
                        previewFile={previewFile}
                        onClose={() => setPreviewFile(null)}
                    />

                    {/* Caja de comentario + botones */}
                    <CommentInputFooter
                        isSubmitting={isSubmitting}
                        isRecording={isRecording}
                        recordingTime={recordingTime}
                        handleAudioRecording={handleAudioRecording}
                        handleFileChange={handleFileChange}
                        handleSubmitComment={handleSubmitComment}
                        commentInputRef={commentInputRef}
                        editingComment={editingComment}
                        isEditing={isEditing}
                        currentStatus={currentStatus}
                    />
                </Box>

                <Box flex={1} p={2} sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }} height="auto">
                    {!resolving ? (
                        <>
                            <TicketInfoPanel
                                tickets={tickets}
                                currentStatus={currentStatus}
                                setCurrentStatus={setCurrentStatus}
                                editingStatus={editingStatus}
                                setEditingStatus={setEditingStatus}
                                statusUpdating={statusUpdating}
                                setStatusUpdating={setStatusUpdating}
                                showReopenConfirm={showReopenConfirm}
                                setShowReopenConfirm={setShowReopenConfirm}
                                confirmReopenTicket={confirmReopenTicket}
                                handleClick={handleClick}
                                anchorEl={anchorEl}
                                handleClose={handleClose}
                                open={open}
                                id={id}
                                setResolving={setResolving}
                                onTicketUpdate={onTicketUpdate}
                                notificationSystemRef={notificationSystemRef}
                                setComments={setComments}
                            />


                            <TicketDocuments
                                ticketDocuments={ticketDocuments}
                                loadingTicketDocuments={loadingTicketDocuments}
                                uploadingFiles={uploadingFiles}
                                pendingTicketFiles={pendingTicketFiles}
                                handleTicketFileUpload={handleTicketFileUpload}
                                removePendingFile={removePendingFile}
                                handleUploadPendingTicketFiles={handleUploadPendingTicketFiles}
                                currentStatus={currentStatus}
                                setPendingTicketFiles={setPendingTicketFiles}
                                setPreviewFile={setPreviewFile}
                            />

                        </>
                    ) : (
                        <TicketResolutionPanel
                            rating={rating}
                            setRating={setRating}
                            feedback={feedback}
                            feedbackInputRef={feedbackInputRef}
                            isSubmitting={isSubmitting}
                            handleConfirmResolution={() => {
                                const value = feedbackInputRef.current?.getValue()?.trim() || '';
                                handleConfirmResolution(value, !!value);
                            }}
                            resolvingLoading={resolvingLoading}
                            setResolving={setResolving}
                        />

                    )}
                </Box>


            </Box >
        </>

    );
};

export default COMMENTS_SUPPORT;
